// RITA-1498 (Nutzungserfassung Phase 1b) — Laufzeit der Erfassung (Konzept 4.1/4.2).
//
// Wird vom UsageTrackingProvider gestartet, sobald GET /usage/config "an" meldet, und beim
// Abmelden/Abschalten gestoppt. Bündelt: Sitzung je Tab, Puffer + Versand, Klick-Zuhörer am
// Dokument (Capture), Fehler-Zuhörer, axios-Interceptor, Flush beim Verlassen des Tabs.
// Alle Zuhörer sind passiv; kein Aufruf hier darf werfen oder die Bedienung bremsen.
import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type { IUsageBatch, IUsageEvent, UsageEnv } from 'global-intern-types';
import { installAxiosUsageInterceptor } from './axiosUsageInterceptor';
import { createBatchQueue, FlushReason } from './batchQueue';
import { buildClickProps, resolveClickTarget } from './clickTarget';
import { resolveDeadClick } from './deadClick';
import { buildSessionSnapshot, SnapshotSettings } from './deviceInfo';
import { installErrorCapture } from './errorCapture';
import type { InteractionHints } from './navigation';
import { createRageClickDetector } from './rageClick';
import type { RageClickDetector } from './rageClick';
import { createSender, FetchLike } from './sender';
import { buildDeadClickProps, buildRageClickProps } from './usageProps';
import { createSessionStore, SessionIdentity, StorageLike } from './session';
import type { UsageTracker } from './tracker';
import { vitalEventFrom, webVitalsBridge } from './webVitals';
import type { WebVitalsBridge } from './webVitals';

type Listener = (event: Event) => void;

export interface RuntimeWindow {
  innerWidth: number;
  innerHeight: number;
  scrollY: number;
  devicePixelRatio: number;
  screen: { width: number; height: number };
  navigator: { userAgent: string; language: string; maxTouchPoints: number };
  sessionStorage?: StorageLike;
  /** Textauswahl des Fensters (RITA-1520); fehlt in älteren Ständen und in Attrappen. */
  getSelection?: () => { isCollapsed: boolean; anchorNode?: Node | null } | null;
  addEventListener(type: string, listener: Listener, options?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, listener: Listener, options?: boolean | EventListenerOptions): void;
}

export interface RuntimeDocument {
  visibilityState: string;
  addEventListener(type: string, listener: Listener, options?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, listener: Listener, options?: boolean | EventListenerOptions): void;
}

export interface UsageRuntimeDeps {
  tracker: UsageTracker;
  hints: InteractionHints;
  fetchImpl: FetchLike;
  getToken: () => string | null;
  getSettings: () => SnapshotSettings;
  appVersion: string;
  env: UsageEnv;
  win: RuntimeWindow;
  doc: RuntimeDocument;
  storage?: StorageLike | null;
  now?: () => number;
  newId?: () => string;
  axiosInstance?: AxiosInstance;
  /** Brücke zu web-vitals (RITA-1515); ein Beobachtersatz je Seitenladung. */
  vitals?: WebVitalsBridge;
  /** Wutklick-Automat (RITA-1520); einer je Laufzeit, beim Stoppen zurückgesetzt. */
  rage?: RageClickDetector;
  /**
   * Zählt dieser Klick für Wut und Tot? Nur echte Zeigerklicks tun das (RITA-1500, W1). In
   * jsdom ist kein per dispatchEvent erzeugtes Ereignis `isTrusted`, deshalb ist die Prüfung
   * für Tests ersetzbar.
   */
  isTrustedClick?: (event: Event) => boolean;
  /** Epoch-Millisekunde, ab der die startTime der Performance-Einträge zählt. */
  timeOrigin?: () => number;
  /** Server hat "aus"/"nicht angemeldet" gemeldet; die Laufzeit hat sich bereits selbst beendet. */
  onStop?: () => void;
}

export interface UsageRuntime {
  stop(): void;
  flush(reason: FlushReason): Promise<void>;
}

const NAV_SELECTOR = 'nav, [data-usage-nav]';

/**
 * Bereiche, deren Klicks NICHT zur Bedienung der Anwendung gehören (RITA-1522). Heute trägt das
 * Attribut nur die Leiste des Heatmap-Overlays: sie liegt über der Anwendung, wird von einem
 * SUPERADMIN bei der Auswertung bedient, und ihre Klicks würden genau die Klickkarte verfälschen,
 * die sie gerade anzeigt (ihre Knöpfe stünden als Plakette und als Fleck in der Wolke der
 * darunter liegenden Seite). Geprüft wird mit `closest`, damit auch ein Klick auf ein Kind
 * (Beschriftung, Symbol) erfasst wird. Die Erfassung wird dadurch NICHT pausiert — nur dieser
 * Teilbaum ist ausgenommen; Abfragen des Overlays erscheinen regulär unter Serveroperationen.
 */
const USAGE_IGNORE_SELECTOR = '[data-usage-ignore]';

/**
 * Liegt das Element in einem ausgenommenen Bereich? (RITA-1522)
 *
 * Genau EINE Regel für Klick und Taste: die Heatmap-Leiste ist auch mit der Tastatur bedienbar,
 * und ein Tastendruck dort ist keine Bedienung der ANWENDUNG — er ließe eine danach folgende
 * Einstellungsänderung als Bedienhandlung statt als Initialisierung gelten.
 */
const istAusgenommen = (element: Element | null): boolean =>
  !!(element && typeof element.closest === 'function' && element.closest(USAGE_IGNORE_SELECTOR));

/**
 * Echter Zeigerklick? `isTrusted` schließt jeden Klick aus dem Code aus (element.click(),
 * Testhelfer), `detail > 0` die Tastenbedienung (Enter auf einem Knopf meldet detail 0) und die
 * Tastenwiederholung. Beides zusammen ist die Vorbedingung für Wut- und Totklicks: sonst zählte
 * eine Schleife im Code als wütender Mensch.
 */
export const isTrustedPointerClick = (event: Event): boolean =>
  event.isTrusted === true && ((event as MouseEvent).detail ?? 0) > 0;

/**
 * performance.timeOrigin gibt es nicht überall (ältere Safari-Stände); dort führt
 * Date.now() - performance.now() zur selben Millisekunde. Fällt auch das aus, bleibt die
 * aktuelle Uhrzeit — die Route wäre dann die zuletzt gesetzte, was der Ersatzroute entspricht.
 */
const defaultTimeOrigin = (): number => {
  try {
    const origin = performance.timeOrigin;
    if (typeof origin === 'number' && Number.isFinite(origin)) return origin;
    return Date.now() - performance.now();
  } catch {
    return Date.now();
  }
};

const safeSessionStorage = (win: RuntimeWindow): StorageLike | null => {
  try {
    return win.sessionStorage ?? null;
  } catch {
    return null;
  }
};

export const startUsageRuntime = ({
  tracker,
  hints,
  fetchImpl,
  getToken,
  getSettings,
  appVersion,
  env,
  win,
  doc,
  storage,
  now = () => Date.now(),
  newId,
  axiosInstance = axios,
  vitals = webVitalsBridge,
  rage = createRageClickDetector(),
  isTrustedClick = isTrustedPointerClick,
  timeOrigin = defaultTimeOrigin,
  onStop,
}: UsageRuntimeDeps): UsageRuntime => {
  const store = createSessionStore({ storage: storage === undefined ? safeSessionStorage(win) : storage, now, newId });
  let session: SessionIdentity = { session_id: '', started_at: 0 };
  let stopped = false;

  const buildBatch = (events: IUsageEvent[]): IUsageBatch => ({
    session: buildSessionSnapshot({
      session,
      settings: getSettings(),
      env,
      appVersion,
      win: {
        innerWidth: win.innerWidth,
        innerHeight: win.innerHeight,
        devicePixelRatio: win.devicePixelRatio,
        screen: win.screen,
      },
      nav: win.navigator,
      tzOffset: new Date(now()).getTimezoneOffset(),
    }),
    events,
  });

  const sender = createSender({ fetchImpl, getToken, buildBatch });

  // Wird unten belegt; teardown braucht die Queue, die Queue braucht teardown (onStop).
  let teardown: () => void = () => undefined;

  const queue = createBatchQueue({
    send: sender.send,
    onStop: () => {
      teardown();
      if (onStop) onStop();
    },
    onSessionConflict: () => store.reset(),
  });

  const openSession = (identity: SessionIdentity, route: string): void => {
    session = identity;
    queue.push({ type: 'session_start', ts: identity.started_at, route });
  };

  const sink = (event: IUsageEvent): void => {
    const state = store.current();
    if (state.ended) {
      // Erst die alte Sitzung abschließen und ihr Bündel bauen (synchron in flush), dann wechseln.
      queue.push({ type: 'session_end', ts: state.ended.last_seen, route: event.route });
      queue.flush('manual').catch(() => undefined);
    }
    if (state.isNew) openSession(state.session, event.route);
    queue.push(event);
  };

  /**
   * Ist DIESER Klick ein Dreifachklick, der Text im Ziel markiert? Das ist Lesen, keine Wut.
   * Bewusst eng (KI-Review !743): nur `detail >= 3` UND eine nicht kollabierte Auswahl, die im
   * geklickten Element liegt. Eine Restauswahl anderswo auf der Seite (kopierter Absatz, markiertes
   * Feld) darf keinen Wutklick unterdrücken — und die Prüfung läuft VOR dem Automaten, damit ein
   * verworfener Klick weder Ausbruch noch Minutendämpfung verbraucht.
   */
  const istTextauswahlKlick = (mouse: MouseEvent, element: Element | null): boolean => {
    if ((mouse.detail ?? 0) < 3) return false;
    try {
      const selection = win.getSelection?.();
      if (!selection || selection.isCollapsed !== false) return false;
      const anchor = selection.anchorNode;
      return !anchor || !element || typeof element.contains !== 'function' ? true : element.contains(anchor);
    } catch {
      return false;
    }
  };

  const onClick: Listener = (event) => {
    try {
      const element = event.target as Element | null;
      // Ausgenommene Bereiche zuerst und vollständig: kein click, kein dead_click, kein
      // rage_click — und auch kein Bedien-Hinweis, denn die Navigationsart der nächsten Seite
      // soll ein Klick in der Leiste nicht färben (RITA-1522).
      if (istAusgenommen(element)) return;
      const inNav = !!(element && typeof element.closest === 'function' && element.closest(NAV_SELECTOR));
      hints.noteInteraction();
      const mouse = event as MouseEvent;
      const target = resolveClickTarget(element);
      if (target) {
        hints.noteClick({ at: now(), inNav });
        tracker.record({
          type: 'click',
          name: target.key,
          props: buildClickProps(
            { clientX: mouse.clientX, pageY: mouse.pageY },
            { innerWidth: win.innerWidth, innerHeight: win.innerHeight, scrollY: win.scrollY },
            target,
          ),
        });
      }
      // Ab hier geht es um Frust (RITA-1520). Dafür zählen nur echte Zeigerklicks — die
      // Klick-Erfassung oben bleibt davon unberührt.
      if (!isTrustedClick(event)) return;
      let ziel: { key: string; kind: string } | null = target;
      if (!ziel) {
        // Kein Bedienelement getroffen: sah es wenigstens bedienbar aus? Ein Totklick erzeugt
        // KEIN click-Ereignis und keine Position (RITA-1500, W3a).
        const dead = resolveDeadClick(element, { clientX: mouse.clientX, clientY: mouse.clientY });
        if (!dead) return;
        tracker.record({
          type: 'dead_click',
          name: dead.key,
          props: buildDeadClickProps({ elementKind: dead.kind }),
        });
        ziel = dead;
      }
      // Dreifachklick zur Textauswahl im Ziel: Lesen, nicht Wut — und der Automat sieht ihn
      // gar nicht erst (sonst wäre der Ausbruch verbraucht und das Ziel eine Minute stumm).
      if (istTextauswahlKlick(mouse, element)) return;
      // Drei Klicks auf dasselbe Ziel binnen einer Sekunde — auch auf einen toten Punkt.
      const burst = rage.note(ziel.key, ziel.kind, { at: now(), x: mouse.clientX, y: mouse.clientY });
      if (!burst) return;
      tracker.record({
        type: 'rage_click',
        name: burst.key,
        props: buildRageClickProps({ elementKind: burst.kind }),
      });
    } catch {
      // nie die Bedienung stören
    }
  };

  const onKeydown: Listener = (event) => {
    if (istAusgenommen(event.target as Element | null)) return;
    hints.noteInteraction();
  };

  const onHide: Listener = () => {
    store.touch();
    queue.flush('hide').catch(() => undefined);
  };

  const onVisibilityChange: Listener = () => {
    if (doc.visibilityState === 'hidden') onHide(new Event('hide'));
  };

  const uninstallErrors = installErrorCapture(win, (source, error) => {
    tracker.reportError(source, error);
  });
  const uninstallAxios = installAxiosUsageInterceptor(
    axiosInstance,
    (input) => {
      tracker.record(input);
    },
    now,
  );

  // Ladeleistung (RITA-1515) — BEWUSST vor den Zuhörern für das Ausblenden: web-vitals meldet
  // INP und CLS beim Ausblenden über einen eigenen visibilitychange-Zuhörer am window im
  // Capture-Modus und läuft damit vor unserem Zuhörer am document. Die Messung liegt also schon
  // im Puffer, wenn flush('hide') das Bündel baut. Die Reihenfolge hier hält das an der Stelle
  // fest, an der sie gilt.
  vitals.attach((metric) => {
    const event = vitalEventFrom(metric, {
      routeAt: tracker.routeAt,
      timeOrigin: timeOrigin(),
      fallbackRoute: tracker.getRoute(),
    });
    if (event) tracker.record(event);
  });

  doc.addEventListener('click', onClick, true);
  doc.addEventListener('keydown', onKeydown, true);
  doc.addEventListener('visibilitychange', onVisibilityChange);
  win.addEventListener('pagehide', onHide);

  teardown = (): void => {
    if (stopped) return;
    stopped = true;
    tracker.detachSink();
    queue.stop();
    doc.removeEventListener('click', onClick, true);
    doc.removeEventListener('keydown', onKeydown, true);
    doc.removeEventListener('visibilitychange', onVisibilityChange);
    win.removeEventListener('pagehide', onHide);
    uninstallErrors();
    uninstallAxios();
    vitals.detach();
    rage.reset();
  };

  const first = store.current();
  session = first.session;
  tracker.attachSink(sink);
  if (first.isNew) openSession(first.session, tracker.getRoute());

  return {
    stop: () => teardown(),
    flush: (reason: FlushReason) => queue.flush(reason),
  };
};
